import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, Info, ShieldAlert, Trash2 } from "lucide-react";

const FREQS = [
  { id: "weekly", label: "Weekly" },
  { id: "fortnightly", label: "Fortnightly" },
  { id: "monthly", label: "Monthly" },
  { id: "irregular", label: "Irregular" },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    preferred_name: user?.preferred_name || "",
    phone: user?.phone || "",
    abn: user?.abn || "",
    business_name: user?.business_name || "",
    gst_registered: !!user?.gst_registered,
    gst_registration_date: user?.gst_registration_date || "",
    default_report_frequency: user?.default_report_frequency || "",
  });
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delPwd, setDelPwd] = useState("");
  const [deleting, setDeleting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/account/profile", form);
      await refreshUser();
      toast.success("Profile saved");
      navigate(-1);
    } catch (e) {
      toast.error(apiError(e));
    }
    setSaving(false);
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", { confirm: delConfirm, password: delPwd });
      toast.success("Your account has been deleted");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setDeleting(false);
  };

  return (
    <AppShell>
      <PageHeader title="Profile" subtitle="Your details and default preferences" onBack={() => navigate(-1)} />
      <div className="px-5 lg:px-8 space-y-4 df-fade-in">
        {/* Personal */}
        <div className="df-card p-5 space-y-4" data-testid="profile-personal-card">
          <div className="text-sm font-semibold text-slate-900">Personal</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="profile-name" value={form.name}
                onChange={(e) => set("name", e.target.value)} className="h-11 rounded-xl" placeholder="Your full name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="preferred_name">Preferred name</Label>
              <Input id="preferred_name" data-testid="profile-preferred-name" value={form.preferred_name}
                onChange={(e) => set("preferred_name", e.target.value)} className="h-11 rounded-xl" placeholder="What we call you" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" data-testid="profile-phone" inputMode="tel" value={form.phone}
                onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-xl" placeholder="04xx xxx xxx" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <div className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 flex items-center gap-2 text-slate-500 text-sm">
                <Mail size={15} className="shrink-0" />
                <span className="truncate" data-testid="profile-email">{user?.email}</span>
              </div>
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Info size={11} /> Email changes are verified separately for security.</p>
            </div>
          </div>
        </div>

        {/* Business / contractor */}
        <div className="df-card p-5 space-y-4" data-testid="profile-business-card">
          <div className="text-sm font-semibold text-slate-900">Business &amp; contractor</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="business_name">Business name</Label>
              <Input id="business_name" data-testid="profile-business-name" value={form.business_name}
                onChange={(e) => set("business_name", e.target.value)} className="h-11 rounded-xl" placeholder="Sole trader / company name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="abn">ABN</Label>
              <Input id="abn" data-testid="profile-abn" inputMode="numeric" value={form.abn}
                onChange={(e) => set("abn", e.target.value)} className="h-11 rounded-xl font-num" placeholder="11 digit ABN" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-slate-900">Registered for GST</div>
              <div className="text-xs text-slate-500">Affects how GST is shown in your records</div>
            </div>
            <Switch data-testid="profile-gst-switch" checked={form.gst_registered}
              onCheckedChange={(v) => set("gst_registered", v)} />
          </div>
          {form.gst_registered && (
            <div className="space-y-1.5" data-testid="profile-gst-date-wrap">
              <Label htmlFor="gst_date">GST registration effective date</Label>
              <Input id="gst_date" type="date" data-testid="profile-gst-date" value={form.gst_registration_date || ""}
                onChange={(e) => set("gst_registration_date", e.target.value)} className="h-11 rounded-xl" />
              {!form.gst_registration_date && (
                <p className="text-[11px] text-amber-600 flex items-center gap-1"><Info size={11} /> Add your GST registration date so GST/BAS reporting reflects when you became registered. Existing records aren't changed.</p>
              )}
            </div>
          )}
        </div>

        {/* Preferences */}
        <div className="df-card p-5 space-y-3" data-testid="profile-preferences-card">
          <div className="text-sm font-semibold text-slate-900">Reporting preferences</div>
          <div className="text-xs text-slate-500 -mt-1">Default reporting frequency for new payment reports (you can change it per practice).</div>
          <div className="flex flex-wrap gap-2 pt-1">
            {FREQS.map((f) => (
              <button key={f.id} type="button" data-testid={`profile-freq-${f.id}`}
                onClick={() => set("default_report_frequency", form.default_report_frequency === f.id ? "" : f.id)}
                className={`px-4 h-10 rounded-full text-sm font-medium df-pressable border transition-colors ${
                  form.default_report_frequency === f.id
                    ? "bg-[#0D786A] text-white border-[#0D786A]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <Button data-testid="profile-save" onClick={save} disabled={saving}
          className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {saving ? "Saving…" : "Save profile"}
        </Button>

        {/* Danger zone — permanent account deletion (Apple Guideline 5.1.1(v)) */}
        <div className="df-card p-5 border border-red-100" data-testid="profile-danger-card">
          <div className="flex items-center gap-3 mb-1">
            <span className="h-10 w-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0"><ShieldAlert size={18} /></span>
            <div>
              <div className="text-sm font-semibold text-slate-900">Delete account</div>
              <div className="text-xs text-slate-500">Permanently remove your account and all data</div>
            </div>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mt-2">
            This permanently deletes your profile, practices, work, payments, expenses, reserves,
            uploaded files and settings, and cancels any active subscription. This action cannot be undone.
          </p>
          <Button data-testid="profile-delete-open" onClick={() => setDelOpen(true)} variant="outline"
            className="w-full h-11 rounded-xl border-red-200 text-red-600 hover:bg-red-50 mt-3">
            <Trash2 size={16} className="mr-1.5" /> Delete account
          </Button>
        </div>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="profile-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><ShieldAlert size={18} /> Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently deletes your Molaroo account and all associated data, and cancels any
              active subscription (Stripe keeps its own legally-required payment records). This action
              is permanent and cannot be undone. Type <strong>DELETE MY ACCOUNT</strong> and your
              password to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            <Input data-testid="profile-delete-confirm" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="DELETE MY ACCOUNT" className="h-11 rounded-xl" />
            <Input data-testid="profile-delete-pwd" type="password" value={delPwd} onChange={(e) => setDelPwd(e.target.value)} placeholder="Your password" className="h-11 rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="profile-delete-confirm-btn" onClick={doDelete}
              disabled={deleting || delConfirm.trim().toUpperCase() !== "DELETE MY ACCOUNT" || !delPwd}
              className="rounded-xl bg-red-600 hover:bg-red-700">{deleting ? "Deleting…" : "Delete forever"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
